import { forwardRef, type CSSProperties, type SelectHTMLAttributes } from 'react'

export interface SelectOption<V extends string = string> {
  value: V
  label: string
  disabled?: boolean
}

interface SelectProps<V extends string = string>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: ReadonlyArray<SelectOption<V>>
  /** Show a non-selectable placeholder option as the first row. */
  placeholder?: string
  /** Match input field width by default (full row) or hug content. */
  fullWidth?: boolean
}

/**
 * Warm-skin native select.
 *
 * Wraps `<select>` so callers don't have to repeat the appearance reset every
 * time. The global reset in `tokens.css` already handles unstyled selects on
 * every page; this primitive exists for consistency and to expose typed
 * options + an optional placeholder.
 *
 * Why native instead of a custom listbox? Native select gets free
 * accessibility + iOS/Android system pickers + screen-reader handling.
 * The downside (the *open* list is OS-themed on Safari/Firefox) is a
 * deliberate compromise documented in tokens.css.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, fullWidth = true, style, ...rest },
  ref,
) {
  const merged: CSSProperties = {
    width: fullWidth ? '100%' : undefined,
    ...style,
  }
  return (
    <select ref={ref} style={merged} {...rest}>
      {placeholder ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </option>
      ))}
    </select>
  )
})
